// Data layer for one async invoice job's stored result (RAT-1914).
import requestApi from 'utils/Request';
import { INVOICE_JOB } from 'utils/StaticApiEndpoints';

/**
 * The result of a finished (or running) invoice job: what was submitted, what
 * carried a tax-registry warning and what was held back.
 *
 * Returns null when the request fails. The panel then says so rather than
 * showing empty groups, which would read as "nothing was skipped".
 */
export const fetchInvoiceJobResult = async (queueId) => {
  try {
    const { data } = await requestApi.get(`${INVOICE_JOB}/${queueId}`);
    return data ?? null;
  } catch {
    return null;
  }
};

/**
 * Counts for the three groups AC4 asks for, derived from the stored result.
 *
 * Deliberately not derived from `done`: it counts attempts in the download
 * worker and successes in the generate worker, so subtracting the failures from
 * it would be right for one and wrong for the other. What every worker does
 * agree on is that a selected merchant is either held back, failed, or
 * invoiced - so that is what is counted. Warnings are a subset of the invoiced
 * ones: a warned invoice still went out.
 *
 * A failed job is the one case where nothing went out at all: both workers throw
 * only when they produced nothing, and the stored result of a failure carries a
 * single message rather than a row per merchant.
 */
export const invoiceJobCounts = (result, status) => {
  const total = result?.total ?? 0;
  const errors = result?.errors ?? [];
  const errorCount = errors.length;
  const skippedCount = result?.skipped?.length ?? 0;

  // The pre-check decides what a run will attempt, so `warned` is captured
  // before any invoice is generated - a merchant can be warned there and then
  // fail on the way out. Counting it in both places listed the same merchant as
  // submitted-with-warnings and as failed at once, and stole the clean count
  // from whichever merchant actually did go out. Only errors that name a
  // merchant can cancel a warning: a run-level failure ("no invoices could be
  // generated") names none, and must not empty the group.
  const failedIds = new Set(
    errors.map((row) => row?.merchantId).filter((id) => id !== undefined),
  );
  const warnedRows = (result?.warned ?? []).filter(
    (row) => !failedIds.has(row?.merchantId),
  );
  const warningCount = warnedRows.length;

  const submitted =
    status === 'failed' ? 0 : Math.max(total - skippedCount - errorCount, 0);
  return {
    total,
    submitted,
    clean: Math.max(submitted - warningCount, 0),
    warningCount,
    warnedRows,
    skippedCount,
    errorCount,
  };
};

/**
 * Where an operator fixes a held-back merchant. The legal name AEAT holds is
 * carried in the URL so the form opens with it already filled in (AC5) - the
 * merchant page owns the save, so nothing is written behind their back.
 */
export const merchantLegalFixPath = (merchantId, registryLegalName) => {
  const base = `/admin/merchants/edit/${merchantId}`;
  return registryLegalName
    ? `${base}?adoptLegalName=${encodeURIComponent(registryLegalName)}`
    : base;
};
